import { array, option, pipe, remote } from '@code-expert/prelude';
import type { CourseOption } from '/imports/api/rpc/courseSelect/shared';
import type { CourseId, CourseUrl, ExamId, ExamStatus, GroupId, SemesterId } from '/imports/domain';
import { courseUrlEq, examIdEq, semesterIdEq } from '/imports/domain';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import { useCachedQuery } from '/imports/ui/hooks/useProcedure';
import { usePublication } from '/imports/ui/hooks/usePublication';

export type { CourseOption };

const useGroupsUpdates = () => usePublication('groups.getUpdates', { method: 'trigger' });
const useCourseUpdates = () =>
  usePublication('courses.getSemesterAndFeature', { method: 'trigger' });
const useExamUpdates = () => usePublication('exams.getUpdates', { method: 'trigger' });

export function useAdminCourses(): remote.Remote<Array<CourseOption>> {
  const { visibleSemesters } = useGlobalContext();

  return useCachedQuery('courseSelect.getAdminCourses', { props: undefined }, [
    useGroupsUpdates(),
    useCourseUpdates(),
    visibleSemesters,
  ])[0];
}

/**
 * Return a list of courses grouped by semester.
 */
export function useEnrolledCourses(): remote.Remote<Array<CourseOption>> {
  const { visibleSemesters } = useGlobalContext();

  return useCachedQuery('courseSelect.getEnrolledCourses', { props: undefined }, [
    useGroupsUpdates(),
    useCourseUpdates(),
    visibleSemesters,
  ])[0];
}

export interface GroupOption {
  _id: GroupId;
  name: string;
  semester: SemesterId;
  courseId: CourseId;
  courseName: string;
  courseUrl: CourseUrl;
  appointments: { active: boolean };
  assistant?: {
    firstname: string;
    lastname: string;
    title?: string;
  };
}

export function useTaGroups(): remote.Remote<Array<GroupOption>> {
  const { visibleSemesters } = useGlobalContext();

  return useCachedQuery('courseSelect.getTaGroups', { props: undefined }, [
    useGroupsUpdates(),
    useCourseUpdates(),
    visibleSemesters,
  ])[0];
}

export interface ExamOption {
  _id: ExamId;
  name: string;
  semester: SemesterId;
  courseId: CourseId;
  courseName: string;
  courseUrl: CourseUrl;
}

export function useExams(): remote.Remote<Array<ExamOption>> {
  const { visibleSemesters } = useGlobalContext();

  return useCachedQuery('courseSelect.getExams', { props: undefined }, [
    useExamUpdates(),
    useCourseUpdates(),
    visibleSemesters,
  ])[0];
}

export const useExam = (semester: SemesterId, courseUrl: CourseUrl, examId: ExamId) =>
  pipe(
    useExams(),
    remote.map((exams) =>
      pipe(
        exams,
        array.findFirst(
          (x) =>
            semesterIdEq.equals(x.semester, semester) &&
            courseUrlEq.equals(x.courseUrl, courseUrl) &&
            examIdEq.equals(x._id, examId),
        ),
        option.map((exam) => ({ exam, exams })),
      ),
    ),
  );

export interface CourseExamOption extends ExamOption {
  status: ExamStatus;
  studentCount: number;
}

export const useCourseExams = (
  courseUrl: CourseUrl,
  semester: SemesterId,
): remote.Remote<Array<CourseExamOption>> =>
  useCachedQuery('courseSelect.getCourseExams', { props: { courseUrl, semester } }, [
    useExamUpdates(),
    useCourseUpdates(),
    courseUrl,
    semester,
  ])[0];
